/** Erro da API em RFC 9457 (`application/problem+json`).
 *
 * O backend nunca devolve texto solto em erro (docs/SPEC.md § 2). Tipar a
 * resposta aqui evita que cada tela invente sua própria forma de ler falha.
 */

export interface ProblemJson {
  type: string;
  title: string;
  status: number;
  detail?: string;
  instance?: string;
  request_id?: string;
  errors?: { field: string; code: string; message: string }[];
}

/** Falha vinda da API, já interpretada. */
export class ApiError extends Error {
  readonly status: number;
  readonly codigo: string;
  readonly requestId?: string;
  readonly camposInvalidos: { field: string; code: string; message: string }[];

  constructor(problema: ProblemJson) {
    super(problema.detail ?? problema.title);
    this.name = "ApiError";
    this.status = problema.status;
    // O código vive no fim do `type` (ex.: .../errors/idempotency-key-reuse).
    this.codigo = problema.type.split("/").pop() ?? "erro-desconhecido";
    this.requestId = problema.request_id;
    this.camposInvalidos = problema.errors ?? [];
  }

  /** Mensagem para o campo, quando a falha foi de validação. */
  mensagemDoCampo(campo: string): string | undefined {
    return this.camposInvalidos.find((e) => e.field === campo)?.message;
  }
}

/** Falha de rede, timeout ou resposta que não é problem+json. */
export class FalhaDeRede extends Error {
  constructor(
    mensagem: string,
    readonly causa?: unknown,
  ) {
    super(mensagem);
    this.name = "FalhaDeRede";
  }
}

const TIPO_PROBLEMA = "application/problem+json";

export async function interpretarErro(resposta: Response): Promise<Error> {
  const tipo = resposta.headers.get("Content-Type") ?? "";

  if (!tipo.includes(TIPO_PROBLEMA)) {
    // Um 502 do nginx ou um proxy no meio do caminho não fala problem+json.
    // Traduzir para uma mensagem útil é melhor que vazar HTML para a tela.
    return new FalhaDeRede(`O servidor respondeu ${resposta.status} em um formato inesperado.`);
  }

  try {
    return new ApiError((await resposta.json()) as ProblemJson);
  } catch (causa) {
    return new FalhaDeRede("Não foi possível interpretar a resposta de erro.", causa);
  }
}
